import React, { useState, useCallback } from 'react';
import Styles from './ContentLibrary.module.scss';
import { useDropzone } from 'react-dropzone';
import {
  FiPlus, FiUpload, FiGlobe, FiFileText, FiLink, FiFilter, FiGrid, FiList,
  FiStar, FiTrash2, FiExternalLink, FiCheck, FiX, FiChevronDown, FiSearch,
  FiRefreshCw, FiImage, FiVideo, FiMic, FiMessageSquare
} from 'react-icons/fi';
import { FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import onBoardingService from 'services/onBoardingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const PLATFORM_CONFIG = {
  twitter: { icon: FaTwitter, label: 'Twitter', color: '#1DA1F2', bgColor: '#E8F5FD' },
  linkedin: { icon: FaLinkedin, label: 'LinkedIn', color: '#0A66C2', bgColor: '#E8F1F8' },
  instagram: { icon: FaInstagram, label: 'Instagram', color: '#E4405F', bgColor: '#FCE8EC' },
  youtube: { icon: FaYoutube, label: 'YouTube', color: '#FF0000', bgColor: '#FFE8E8' },
  tiktok: { icon: FaTiktok, label: 'TikTok', color: '#000000', bgColor: '#F0F0F0' },
  website: { icon: FiGlobe, label: 'Blog', color: '#10B981', bgColor: '#ECFDF5' },
  upload: { icon: FiFileText, label: 'Uploaded', color: '#6366F1', bgColor: '#EEF2FF' },
  vocable: { icon: FiFileText, label: 'Campaigns', color: '#F97316', bgColor: '#FFF7ED' },
};

const CONTENT_TYPES = {
  social_post: 'Social Post',
  blog_post: 'Blog Post',
  email: 'Email',
  newsletter: 'Newsletter',
  landing_page: 'Landing Page',
  case_study: 'Case Study',
  whitepaper: 'Whitepaper',
  video_script: 'Video Script',
  podcast_script: 'Podcast Script',
  ad_copy: 'Ad Copy',
  brand_material: 'Brand Material',
  testimonial: 'Testimonial',
};

// Content types available for file uploads (subset most relevant for uploads)
const UPLOAD_CONTENT_TYPES = [
  { value: 'blog_post', label: 'Blog Post', desc: 'Articles, blog content' },
  { value: 'case_study', label: 'Case Study', desc: 'Customer success stories' },
  { value: 'whitepaper', label: 'Whitepaper', desc: 'In-depth guides, research' },
  { value: 'newsletter', label: 'Newsletter', desc: 'Email newsletters' },
  { value: 'email', label: 'Email', desc: 'Marketing emails' },
  { value: 'brand_material', label: 'Brand Material', desc: 'Brand guidelines, decks' },
  { value: 'landing_page', label: 'Landing Page', desc: 'Website copy' },
  { value: 'ad_copy', label: 'Ad Copy', desc: 'Advertisements' },
  { value: 'video_script', label: 'Video Script', desc: 'Video content scripts' },
  { value: 'podcast_script', label: 'Podcast Script', desc: 'Podcast scripts' },
];

// Helper to parse content with transcript marker
const parseContentWithTranscript = (content) => {
  if (!content) return { caption: '', transcript: null };

  const transcriptMarker = '[VIDEO TRANSCRIPT]';
  const markerIndex = content.indexOf(transcriptMarker);

  if (markerIndex === -1) {
    return { caption: content, transcript: null };
  }

  const caption = content.substring(0, markerIndex).trim();
  const transcript = content.substring(markerIndex + transcriptMarker.length).trim();

  return { caption, transcript };
};

const ContentLibrary = ({ brandId, samples = [], stats, onRefresh, data, onUpdate }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [expandedSample, setExpandedSample] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  // File upload with content type selection
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState('blog_post');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Filter samples based on active filter and search
  const filteredSamples = samples.filter(sample => {
    // Platform/source filter
    if (activeFilter !== 'all') {
      const samplePlatform = sample.platform || 'upload';
      if (activeFilter === 'social') {
        if (!['twitter', 'linkedin', 'instagram', 'youtube', 'tiktok'].includes(samplePlatform)) {
          return false;
        }
      } else if (samplePlatform !== activeFilter) {
        return false;
      }
    }

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const content = sample.content || '';
      const title = sample.title || '';
      return content.toLowerCase().includes(searchLower) || title.toLowerCase().includes(searchLower);
    }

    return true;
  });

  // Group samples by platform for the sidebar stats
  const platformCounts = samples.reduce((acc, sample) => {
    const platform = sample.platform || 'upload';
    acc[platform] = (acc[platform] || 0) + 1;
    return acc;
  }, {});

  // File upload handler - shows content type selection modal
  const onDrop = useCallback((acceptedFiles) => {
    if (!brandId) {
      errorToast('Please complete the previous steps first');
      return;
    }

    if (acceptedFiles.length > 0) {
      setPendingFiles(acceptedFiles);
      setSelectedContentType('blog_post');
      setShowUploadModal(true);
      setShowAddMenu(false);
    }
  }, [brandId]);

  // Actually upload files with selected content type
  const handleConfirmUpload = async () => {
    if (!brandId || pendingFiles.length === 0) return;

    setIsUploading(true);
    setShowUploadModal(false);

    try {
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('content_type', selectedContentType);
        formData.append('title', file.name);

        await onBoardingService.uploadContentSample(brandId, formData);
      }

      successToast(`Successfully uploaded ${pendingFiles.length} file(s)`);
      onRefresh();
    } catch (error) {
      console.error('Upload error:', error);
      errorToast('Failed to upload files');
    } finally {
      setIsUploading(false);
      setPendingFiles([]);
    }
  };

  // Cancel upload
  const handleCancelUpload = () => {
    setShowUploadModal(false);
    setPendingFiles([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    multiple: true,
    noClick: true,
  });

  // Load import preview
  const loadImportPreview = async () => {
    if (!brandId) return;

    try {
      setIsLoadingPreview(true);
      const response = await onBoardingService.previewDocumentImport(brandId);
      if (response.data.success) {
        setImportPreview(response.data.data);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Import documents from campaigns
  const handleImportDocuments = async () => {
    if (!brandId) return;

    try {
      setIsImporting(true);
      const response = await onBoardingService.importDocuments(brandId);

      if (response.data.success) {
        successToast(`Imported ${response.data.data.synced} documents`);
        onRefresh();
        loadImportPreview();
      }
    } catch (error) {
      errorToast('Failed to import documents');
    } finally {
      setIsImporting(false);
    }
  };

  // Delete sample
  const handleDeleteSample = async (sampleId) => {
    try {
      await onBoardingService.deleteContentSample(brandId, sampleId);
      successToast('Content removed');
      onRefresh();
    } catch (error) {
      errorToast('Failed to remove content');
    }
  };

  // Toggle sample star (for now just visual)
  const handleStarSample = (sampleId) => {
    // This would be implemented with backend support
    successToast('Marked as top performer');
  };

  // Render platform icon
  const renderPlatformIcon = (platform) => {
    const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.upload;
    const Icon = config.icon;
    return (
      <div
        className={Styles.platformBadge}
        style={{ backgroundColor: config.bgColor, color: config.color }}
      >
        <Icon />
      </div>
    );
  };

  // Format engagement metrics
  const formatEngagement = (metadata) => {
    if (!metadata?.engagement) return null;
    const { likes, comments, shares, views, retweets, replies } = metadata.engagement;

    const metrics = [];
    if (likes) metrics.push(`${formatNumber(likes)} likes`);
    if (comments || replies) metrics.push(`${formatNumber(comments || replies)} comments`);
    if (shares || retweets) metrics.push(`${formatNumber(shares || retweets)} shares`);
    if (views) metrics.push(`${formatNumber(views)} views`);

    return metrics.length > 0 ? metrics.join(' · ') : null;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Render single content card
  const renderContentCard = (sample) => {
    const platform = sample.platform || 'upload';
    const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.upload;
    const engagement = formatEngagement(sample.metadata);
    // Don't show thumbnails for TikTok/YouTube - show caption + transcript text instead (for content training)
    const hasThumbnail = sample.metadata?.thumbnail_url && !['tiktok', 'youtube'].includes(sample.platform);
    const isExpanded = expandedSample === sample.id;

    return (
      <div
        key={sample.id}
        className={`${Styles.contentCard} ${isExpanded ? Styles.expanded : ''}`}
        onClick={() => setExpandedSample(isExpanded ? null : sample.id)}
      >
        {/* Thumbnail for visual content */}
        {hasThumbnail && (
          <div className={Styles.thumbnail}>
            <img src={sample.metadata.thumbnail_url} alt="" />
            {platform === 'youtube' && <FiVideo className={Styles.videoIcon} />}
          </div>
        )}

        <div className={Styles.cardContent}>
          {/* Header with platform and actions */}
          <div className={Styles.cardHeader}>
            {renderPlatformIcon(platform)}
            <span className={Styles.platformLabel}>{config.label}</span>
            {sample.quality_score && (
              <span className={Styles.qualityBadge}>
                {Math.round(sample.quality_score * 100)}%
              </span>
            )}
            <div className={Styles.cardActions}>
              <button
                className={Styles.actionBtn}
                onClick={(e) => { e.stopPropagation(); handleStarSample(sample.id); }}
                title="Mark as top performer"
              >
                <FiStar />
              </button>
              {sample.source_url && (
                <a
                  href={sample.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={Styles.actionBtn}
                  onClick={(e) => e.stopPropagation()}
                  title="View original"
                >
                  <FiExternalLink />
                </a>
              )}
              <button
                className={`${Styles.actionBtn} ${Styles.deleteBtn}`}
                onClick={(e) => { e.stopPropagation(); handleDeleteSample(sample.id); }}
                title="Remove"
              >
                <FiTrash2 />
              </button>
            </div>
          </div>

          {/* Content preview */}
          <div className={Styles.contentPreview}>
            {(() => {
              const { caption, transcript } = parseContentWithTranscript(sample.content);
              const hasTranscript = transcript !== null;

              return (
                <>
                  {/* Caption/Description section */}
                  {caption ? (
                    <div className={Styles.captionSection}>
                      {hasTranscript && (
                        <span className={Styles.contentLabel}>
                          <FiMessageSquare className={Styles.labelIcon} />
                          Caption
                        </span>
                      )}
                      <p className={isExpanded ? Styles.expanded : ''}>
                        {caption}
                      </p>
                    </div>
                  ) : null}

                  {/* Transcript section */}
                  {hasTranscript && (
                    <div className={Styles.transcriptSection}>
                      <span className={Styles.contentLabel}>
                        <FiMic className={Styles.labelIcon} />
                        Transcript
                      </span>
                      <p className={isExpanded ? Styles.expanded : ''}>
                        {transcript}
                      </p>
                    </div>
                  )}

                  {/* Fallback for no content */}
                  {!caption && !hasTranscript && (
                    <p className={isExpanded ? Styles.expanded : ''}>
                      {sample.title || 'No content'}
                    </p>
                  )}
                </>
              );
            })()}
          </div>

          {/* Footer with metadata */}
          <div className={Styles.cardFooter}>
            {engagement && (
              <span className={Styles.engagement}>{engagement}</span>
            )}
            {sample.word_count && (
              <span className={Styles.wordCount}>{sample.word_count} words</span>
            )}
            {sample.content_type && sample.content_type !== 'social_post' && (
              <span className={Styles.contentType}>
                {CONTENT_TYPES[sample.content_type] || sample.content_type}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render list view row
  const renderContentRow = (sample) => {
    const platform = sample.platform || 'upload';
    const config = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.upload;
    const engagement = formatEngagement(sample.metadata);

    return (
      <div key={sample.id} className={Styles.contentRow}>
        {renderPlatformIcon(platform)}
        <div className={Styles.rowContent}>
          <p className={Styles.rowText}>
            {(sample.content || sample.title || 'No content').substring(0, 150)}
            {(sample.content?.length > 150) && '...'}
          </p>
          <div className={Styles.rowMeta}>
            {engagement && <span>{engagement}</span>}
            {sample.word_count && <span>{sample.word_count} words</span>}
          </div>
        </div>
        <div className={Styles.rowActions}>
          <button className={Styles.actionBtn} onClick={() => handleStarSample(sample.id)}>
            <FiStar />
          </button>
          <button className={`${Styles.actionBtn} ${Styles.deleteBtn}`} onClick={() => handleDeleteSample(sample.id)}>
            <FiTrash2 />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={Styles.container} {...getRootProps()}>
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <div className={Styles.dragOverlay}>
          <FiUpload className={Styles.dragIcon} />
          <p>Drop files to upload</p>
        </div>
      )}

      {/* Toolbar */}
      <div className={Styles.toolbar}>
        <div className={Styles.toolbarLeft}>
          <div className={Styles.searchBox}>
            <FiSearch className={Styles.searchIcon} />
            <input
              type="text"
              placeholder="Search content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className={Styles.clearSearch} onClick={() => setSearchQuery('')}>
                <FiX />
              </button>
            )}
          </div>
        </div>

        <div className={Styles.toolbarRight}>
          <div className={Styles.viewToggle}>
            <button
              className={`${Styles.viewBtn} ${viewMode === 'grid' ? Styles.active : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              className={`${Styles.viewBtn} ${viewMode === 'list' ? Styles.active : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>

          <button className={Styles.refreshBtn} onClick={onRefresh}>
            <FiRefreshCw />
          </button>

          <div className={Styles.addMenuWrapper}>
            <button
              className={Styles.addBtn}
              onClick={() => setShowAddMenu(!showAddMenu)}
            >
              <FiPlus />
              Add Content
              <FiChevronDown className={showAddMenu ? Styles.rotated : ''} />
            </button>

            {showAddMenu && (
              <div className={Styles.addMenu}>
                <button
                  className={Styles.addMenuItem}
                  onClick={() => {
                    document.querySelector('input[type="file"]')?.click();
                    setShowAddMenu(false);
                  }}
                >
                  <FiUpload />
                  <div>
                    <span className={Styles.menuItemTitle}>Upload Files</span>
                    <span className={Styles.menuItemDesc}>PDF, DOC, TXT files</span>
                  </div>
                </button>

                <button
                  className={Styles.addMenuItem}
                  onClick={() => {
                    loadImportPreview();
                    setShowAddMenu(false);
                  }}
                >
                  <FiFileText />
                  <div>
                    <span className={Styles.menuItemTitle}>Import from Campaigns</span>
                    <span className={Styles.menuItemDesc}>Your published content</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className={Styles.filterTabs}>
        <button
          className={`${Styles.filterTab} ${activeFilter === 'all' ? Styles.active : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All Sources
          <span className={Styles.filterCount}>{samples.length}</span>
        </button>
        <button
          className={`${Styles.filterTab} ${activeFilter === 'social' ? Styles.active : ''}`}
          onClick={() => setActiveFilter('social')}
        >
          Social Media
          <span className={Styles.filterCount}>
            {(platformCounts.twitter || 0) + (platformCounts.linkedin || 0) +
             (platformCounts.instagram || 0) + (platformCounts.youtube || 0) +
             (platformCounts.tiktok || 0)}
          </span>
        </button>
        {platformCounts.website > 0 && (
          <button
            className={`${Styles.filterTab} ${activeFilter === 'website' ? Styles.active : ''}`}
            onClick={() => setActiveFilter('website')}
          >
            <FiGlobe />
            Blog
            <span className={Styles.filterCount}>{platformCounts.website}</span>
          </button>
        )}
        {platformCounts.upload > 0 && (
          <button
            className={`${Styles.filterTab} ${activeFilter === 'upload' ? Styles.active : ''}`}
            onClick={() => setActiveFilter('upload')}
          >
            <FiFileText />
            Uploads
            <span className={Styles.filterCount}>{platformCounts.upload}</span>
          </button>
        )}
        {Object.entries(platformCounts)
          .filter(([platform]) => ['twitter', 'linkedin', 'instagram', 'youtube', 'tiktok'].includes(platform))
          .map(([platform, count]) => {
            const config = PLATFORM_CONFIG[platform];
            const Icon = config.icon;
            return (
              <button
                key={platform}
                className={`${Styles.filterTab} ${activeFilter === platform ? Styles.active : ''}`}
                onClick={() => setActiveFilter(platform)}
                style={activeFilter === platform ? { borderColor: config.color } : {}}
              >
                <Icon style={{ color: config.color }} />
                {config.label}
                <span className={Styles.filterCount}>{count}</span>
              </button>
            );
          })}
      </div>

      {/* Import Preview Panel */}
      {importPreview && (
        <div className={Styles.importPanel}>
          <div className={Styles.importHeader}>
            <h4>Import from Campaigns</h4>
            <button className={Styles.closeImport} onClick={() => setImportPreview(null)}>
              <FiX />
            </button>
          </div>
          <div className={Styles.importStats}>
            <span>{importPreview.total_eligible} eligible</span>
            <span>{importPreview.already_synced} imported</span>
            <span className={Styles.highlight}>{importPreview.to_sync} ready</span>
          </div>
          {importPreview.to_sync > 0 ? (
            <button
              className={Styles.importBtn}
              onClick={handleImportDocuments}
              disabled={isImporting}
            >
              {isImporting ? <FiRefreshCw className={Styles.spinning} /> : <FiFileText />}
              Import {importPreview.to_sync} Documents
            </button>
          ) : (
            <p className={Styles.importComplete}>All documents imported</p>
          )}
        </div>
      )}

      {/* Upload Modal - Content Type Selection */}
      {showUploadModal && (
        <div className={Styles.uploadModalOverlay} onClick={handleCancelUpload}>
          <div className={Styles.uploadModal} onClick={(e) => e.stopPropagation()}>
            <div className={Styles.uploadModalHeader}>
              <h4>Upload {pendingFiles.length} File{pendingFiles.length > 1 ? 's' : ''}</h4>
              <button className={Styles.closeModal} onClick={handleCancelUpload}>
                <FiX />
              </button>
            </div>

            <div className={Styles.uploadModalBody}>
              <div className={Styles.filesList}>
                {pendingFiles.map((file, index) => (
                  <div key={index} className={Styles.fileItem}>
                    <FiFileText />
                    <span>{file.name}</span>
                    <span className={Styles.fileSize}>
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                ))}
              </div>

              <div className={Styles.contentTypeSection}>
                <label className={Styles.contentTypeLabel}>
                  What type of content is this?
                </label>
                <div className={Styles.contentTypeGrid}>
                  {UPLOAD_CONTENT_TYPES.map((type) => (
                    <button
                      key={type.value}
                      className={`${Styles.contentTypeOption} ${selectedContentType === type.value ? Styles.selected : ''}`}
                      onClick={() => setSelectedContentType(type.value)}
                    >
                      <span className={Styles.optionLabel}>{type.label}</span>
                      <span className={Styles.optionDesc}>{type.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={Styles.uploadModalFooter}>
              <button className={Styles.cancelBtn} onClick={handleCancelUpload}>
                Cancel
              </button>
              <button className={Styles.confirmUploadBtn} onClick={handleConfirmUpload}>
                <FiUpload />
                Upload as {CONTENT_TYPES[selectedContentType]}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Grid/List */}
      {filteredSamples.length > 0 ? (
        <div className={viewMode === 'grid' ? Styles.contentGrid : Styles.contentList}>
          {viewMode === 'grid'
            ? filteredSamples.map(renderContentCard)
            : filteredSamples.map(renderContentRow)
          }
        </div>
      ) : (
        <div className={Styles.emptyState}>
          {samples.length === 0 ? (
            <>
              <div className={Styles.emptyIcon}>
                <FiUpload />
              </div>
              <h3>No content yet</h3>
              <p>Add content from social media, upload files, or import from your campaigns to start training your AI.</p>
              <button
                className={Styles.emptyAddBtn}
                onClick={() => setShowAddMenu(true)}
              >
                <FiPlus />
                Add Your First Content
              </button>
            </>
          ) : (
            <>
              <FiSearch className={Styles.emptySearchIcon} />
              <h3>No results found</h3>
              <p>Try adjusting your search or filters</p>
              <button
                className={Styles.clearFiltersBtn}
                onClick={() => { setSearchQuery(''); setActiveFilter('all'); }}
              >
                Clear Filters
              </button>
            </>
          )}
        </div>
      )}

      {/* Upload indicator */}
      {isUploading && (
        <div className={Styles.uploadingIndicator}>
          <FiRefreshCw className={Styles.spinning} />
          <span>Uploading...</span>
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
